import React, { createContext, useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { FormikProps, useFormik } from "formik";

import {
  useAppDispatch,
  useAppSelector,
  selectApplication,
  AppDispatch,
  selectProgram,
  selectVerificationState,
  selectRefData,
  useGetProgramEventListQuery,
  useGetEligibleEvacuationCoordinatesQuery,
} from "../../store";
import {
  createEvacuationLocationHandlers,
  createPaymentHandlers,
  createPersonalInformationHandlers,
} from "./handlers";
import { useGenerationFormSubmit } from "../hooks";
import { initialValues, generationFormValidation } from "../formik-config";

export interface GenerationFormContextType {
  dispatch: AppDispatch;
  application: any;
  program: any;
  verificationState: any;
  eventList: any[];
  eligibleEvacuationCoordinates: any;
  refDataList: any[];
  getRefData: (params: { category: string }) => any[];
  handleRootChange: (name: string, value: any) => void;
  handleEmailChange: (name: string, value: string) => void;
  handleETransferChange: (name: string, value: any) => void;
  handleVerificationChange: (name: string, value: any) => void;
  handlePrimaryContactInfoChange: (name: string, value: any) => void;
  handlePaymentInformationMobileAndPhoneChange: (
    name: string,
    value: any
  ) => void;
  handleEvacuationLocationChange: (
    value: boolean,
    coordinatesData: any
  ) => void;
  handleBankAccountDetailsToggle: (name: string, value: any) => void;
  disableGeneration: boolean;
  setDisableGeneration: React.Dispatch<React.SetStateAction<boolean>>;
  isReady: boolean;
  form: FormikProps<any>;
}

export const GenerationFormContext =
  createContext<GenerationFormContextType | null>(
    null
  ) as React.Context<GenerationFormContextType | null>;

// Provider component
export const GenerationFormProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const dispatch = useAppDispatch();
  const application = useAppSelector(selectApplication);
  const program = useSelector(selectProgram);
  const verificationState = useSelector(selectVerificationState);
  const refDataList = useAppSelector(selectRefData);

  // Redux API
  const { data: eligibleEvacuationCoordinates } =
    useGetEligibleEvacuationCoordinatesQuery();
  const { data: eventList } = useGetProgramEventListQuery(program?.id ?? "", {
    skip: !program?.id,
  });

  // React state
  const [disableGeneration, setDisableGeneration] = useState(false);
  const [lastProgramId, setLastProgramId] = useState(program?.programId);

  useEffect(() => {
    setDisableGeneration(!!application?.uniqueApplicationCode);
  }, [setDisableGeneration, application?.uniqueApplicationCode]);

  // Re-initializes form on program change.
  const reinitialize = useMemo(() => {
    const programChanged = program?.programId !== lastProgramId;
    return programChanged;
  }, [program?.programId, lastProgramId]);

  // Monitors for program change.
  useEffect(() => {
    if (program?.programId !== lastProgramId) {
      setLastProgramId(program?.programId);
    }
  }, [program?.programId, lastProgramId]);

  const { handleSubmit } = useGenerationFormSubmit(null, application);

  const form = useFormik({
    initialValues: initialValues(
      application,
      program?.programId ?? "",
      eventList ? eventList[0]?.code : "",
      eligibleEvacuationCoordinates,
      verificationState?.verificationStatus
    ),
    validationSchema: generationFormValidation,
    enableReinitialize: reinitialize,
    validateOnChange: false,
    validateOnBlur: true,
    validateOnMount: true,
    onSubmit: () => {
      handleSubmit(form);
    },
  });

  const handlerDependencies = { form, dispatch };

  const evacuationLocationHandlers =
    createEvacuationLocationHandlers(handlerDependencies);
  const paymentHandlers = createPaymentHandlers(handlerDependencies);
  const personalInformationHandlers =
    createPersonalInformationHandlers(handlerDependencies);

  const handleRootChange = (name: string, value: any) => {
    form.setFieldValue(name, value);

    if (name === "programId") {
      form.setFieldValue("applicationType", "");
    }
  };

  const getRefData = ({ category }: { category: string }) => {
    return refDataList.filter((data: any) => data.categoryCode === category);
  };

  const contextValue: GenerationFormContextType = {
    dispatch,
    application,
    program,
    verificationState,
    eventList: eventList || [],
    eligibleEvacuationCoordinates,
    refDataList,
    getRefData,
    handleRootChange,
    handleEmailChange: personalInformationHandlers.handleEmailChange,
    handlePrimaryContactInfoChange:
      personalInformationHandlers.handlePrimaryContactInfoChange,
    handleETransferChange: paymentHandlers.handleETransferChange,
    handleVerificationChange: paymentHandlers.handleVerificationChange,
    handlePaymentInformationMobileAndPhoneChange:
      paymentHandlers.handlePaymentInformationMobileAndPhoneChange,
    handleBankAccountDetailsToggle:
      paymentHandlers.handleBankAccountDetailsToggle,
    handleEvacuationLocationChange:
      evacuationLocationHandlers.handleEvacuationLocationChange,
    disableGeneration,
    setDisableGeneration,
    isReady: !!application,
    form,
  };

  return (
    <GenerationFormContext.Provider value={contextValue}>
      {children}
    </GenerationFormContext.Provider>
  );
};
